import type {
  InvoiceOrderAddItem,
  InvoiceOrderDismissReason,
  InvoiceOrderLine,
  InvoiceOrderType,
  InvoiceOrders,
  InvoiceService,
} from '@/shared/types/billing';

/**
 * Pure helpers behind the invoice "Pending orders" section (PROD-2591).
 *
 * The Orders read is line-shaped: a vaccine arrives as a product line and, when
 * the location has a priced 90471, a separate administration line. A biller
 * thinks of that as ONE order, so the section shows one row per order and acts
 * on all of its lines together (the two are added, dismissed and selected
 * together by default). `groupOrderLines` does that folding.
 */

/** True when the Orders read has anything to show at all (not a device booking,
 *  and the plan has at least one order in any state). */
export function hasAnyOrders(data: InvoiceOrders | undefined): data is InvoiceOrders {
  return (
    !!data &&
    data.state !== 'NoEncounter' &&
    data.pending.length + data.added.length + data.dismissed.length > 0
  );
}

/** A service chosen for an Ambiguous line, keyed by `sourceOrderKey`. */
export type ServiceChoices = Readonly<Record<string, string | undefined>>;

export interface OrderGroup {
  /** Key of the order; for a vaccine, its lines' key without `:product` / `:admin`. */
  key: string;
  /** The line that names the row: the vaccine product, or the only line. */
  primary: InvoiceOrderLine;
  lines: InvoiceOrderLine[];
  orderType: InvoiceOrderType;
}

const VACCINE_ROLE_SUFFIX = /:(product|admin)$/;

/** `v1:vaccine:90686:product` -> `v1:vaccine:90686`; other keys unchanged. */
export function orderGroupKey(line: InvoiceOrderLine): string {
  return line.orderType === 'Vaccine'
    ? line.sourceOrderKey.replace(VACCINE_ROLE_SUFFIX, '')
    : line.sourceOrderKey;
}

/** Folds a vaccine's product + administration lines into one group, keeping order. */
export function groupOrderLines(lines: readonly InvoiceOrderLine[]): OrderGroup[] {
  const groups = new Map<string, OrderGroup>();
  for (const line of lines) {
    const key = orderGroupKey(line);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { key, primary: line, lines: [line], orderType: line.orderType });
      continue;
    }
    existing.lines.push(line);
    if (line.chargeRole === 'VaccineProduct') existing.primary = line;
  }
  for (const g of groups.values()) {
    // Product first, administration second, so the row reads "95.00 + 45.00 admin".
    g.lines.sort((a, b) => roleRank(a) - roleRank(b));
  }
  return [...groups.values()];
}

function roleRank(line: InvoiceOrderLine): number {
  return line.chargeRole === 'VaccineAdministration' ? 1 : 0;
}

/** The fee a line would add: its indicative price, or the chosen candidate's fee. */
export function lineFee(line: InvoiceOrderLine, choices: ServiceChoices): number | null {
  if (line.priceState === 'Priced') return line.indicativePrice;
  if (line.priceState === 'Ambiguous') {
    const chosen = choices[line.sourceOrderKey];
    const candidate = line.serviceCandidates.find((c) => c.id === chosen);
    return candidate ? candidate.fee : null;
  }
  return null;
}

export type GroupReadiness =
  /** Every line is priced or has a chosen service: it can be added. */
  | 'ready'
  /** An Ambiguous line still needs a service picked. */
  | 'needsChoice'
  /** A line has no price anywhere: set a price first. It can still be dismissed. */
  | 'noPrice';

export function groupReadiness(group: OrderGroup, choices: ServiceChoices): GroupReadiness {
  if (group.lines.some((l) => l.priceState === 'NoPrice')) return 'noPrice';
  if (group.lines.some((l) => l.priceState === 'Ambiguous' && !choices[l.sourceOrderKey])) {
    return 'needsChoice';
  }
  return 'ready';
}

/**
 * PROD-2858: the package discount a group's lines carry (self-pay clinic panel
 * lines only), as a positive amount, or null when there is none.
 */
export function groupPackageDiscount(group: OrderGroup): number | null {
  const total = group.lines.reduce((sum, l) => sum + (l.packageDiscount && l.packageDiscount > 0 ? l.packageDiscount : 0), 0);
  return total > 0 ? Math.round(total * 100) / 100 : null;
}

/** PROD-2858: "No CPT: not claimable" when any of the group's lines has no CPT on an insured invoice. */
export function notClaimableReason(group: OrderGroup): string | null {
  const line = group.lines.find((l) => l.notClaimable);
  return line ? line.notClaimableReason?.trim() || 'No CPT: not claimable' : null;
}

/**
 * PROD-2848: the lab line's context under its name, or null.
 *   clinic panel member  -> "Part of Serum Kidney Function"
 *   reflex / unsolicited -> "Reflex test, performed by the lab" / "Added by the lab"
 */
export function labLineContext(line: InvoiceOrderLine): string | null {
  const panel = line.labPanelName?.trim();
  switch (line.lineKind) {
    case 'Test':
    case 'Panel':
      return panel ? `Part of ${panel}` : null;
    case 'Reflex':
      return 'Reflex test, performed by the lab';
    case 'Unsolicited':
      return 'Added by the lab';
    default:
      return null;
  }
}

/** Sum of the group's line fees, or null while any line has no fee yet. */
export function groupFee(group: OrderGroup, choices: ServiceChoices): number | null {
  let total = 0;
  for (const line of group.lines) {
    const fee = lineFee(line, choices);
    if (fee === null) return null;
    total += fee;
  }
  return total;
}

/** The `add` body items for these groups. `serviceId` only where a choice is needed. */
export function addItemsFor(
  groups: readonly OrderGroup[],
  choices: ServiceChoices,
): InvoiceOrderAddItem[] {
  return groups.flatMap((g) =>
    g.lines.map((l) => {
      const chosen = l.priceState === 'Ambiguous' ? choices[l.sourceOrderKey] : undefined;
      return chosen ? { sourceOrderKey: l.sourceOrderKey, serviceId: chosen } : { sourceOrderKey: l.sourceOrderKey };
    }),
  );
}

/**
 * PROD-2678 (ported from practitioner-react PROD-2594): a NoPrice order is added
 * through the add-service picker, and the picked service rides as `serviceId`
 * on every NoPrice line of the order (the contract requires one there). Other
 * lines are sent as `addItemsFor` would send them.
 */
export function addItemsWithPickedService(
  group: OrderGroup,
  choices: ServiceChoices,
  serviceId: string,
): InvoiceOrderAddItem[] {
  return group.lines.map((l) => {
    if (l.priceState === 'NoPrice') return { sourceOrderKey: l.sourceOrderKey, serviceId };
    const chosen = l.priceState === 'Ambiguous' ? choices[l.sourceOrderKey] : undefined;
    return chosen ? { sourceOrderKey: l.sourceOrderKey, serviceId: chosen } : { sourceOrderKey: l.sourceOrderKey };
  });
}

/** The `dismiss` / `restore` body items for these groups (every line). */
export function itemRefsFor(groups: readonly OrderGroup[]) {
  return groups.flatMap((g) => g.lines.map((l) => ({ sourceOrderKey: l.sourceOrderKey })));
}

/**
 * `85025 • CPT`: the code and system of the Service the line bills, so the row
 * reads the same as the invoice line it becomes (PROD-3156). A lab test linked
 * to a clinic Service bills that Service's own code (ApoA-1 `016873 • IHL` is
 * billed as `310101 • Internal`). Without a matched Service it is the order's
 * own code.
 */
export function codeLabel(line: InvoiceOrderLine): string {
  const billed = line.serviceId
    ? line.serviceCandidates?.find((c) => c.id === line.serviceId)
    : undefined;
  if (billed?.code) return [billed.code, billed.codeSystem].filter(Boolean).join(' • ');
  return [line.code, line.codeSystem].filter(Boolean).join(' • ');
}

export const DISMISS_REASON_LABEL: Record<InvoiceOrderDismissReason, string> = {
  NotPerformed: 'Not performed',
  BilledElsewhere: 'Billed elsewhere',
  IncludedInPackage: 'Included in package',
  Other: 'Other',
};

export const DISMISS_REASONS: InvoiceOrderDismissReason[] = [
  'NotPerformed',
  'BilledElsewhere',
  'IncludedInPackage',
  'Other',
];

/** Shown on every Dismiss affordance. */
export const DISMISS_TOOLTIP = 'Not billed on this invoice. The clinical order is not cancelled.';

/**
 * Billing writes these timestamps from `DateTime.UtcNow` and serialises them
 * without an offset, so a bare `2026-09-24T06:42:00` is UTC. An explicit
 * offset is honoured.
 */
export function parseOrderTimestamp(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `10:42` today, `23 Sep, 10:42` on another day. Local time. */
export function formatOrderTime(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parseOrderTimestamp(iso);
  if (!d) return '';
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return time;
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${day}, ${time}`;
}

/** "CBC and HbA1c", "CBC, HbA1c and ECG" */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Order to Invoice (PROD-2592): what the services table knows about a line
 * that was added from a pending order, keyed by `sourceOrderKey`. Built by the
 * host page from the Orders read (`added[]`).
 */
export interface InvoiceServiceOrderInfo {
  orderType: InvoiceOrderType;
  addedOn: string | null;
  /** The order left the plan after it was added. */
  removedFromPlan: boolean;
  /** The biller chose Keep for this session (UI only; nothing is sent). */
  kept: boolean;
}

/** A line added from a pending order (`billableSource === 'Manual'`). */
export function isOrderLine(s: InvoiceService): s is InvoiceService & { sourceOrderKey: string } {
  return s.billableSource === 'Manual' && Boolean(s.sourceOrderKey);
}

/** The removed-from-plan flag is still waiting for Keep or Drop. */
export function needsKeepOrDrop(info: InvoiceServiceOrderInfo | undefined): boolean {
  return Boolean(info?.removedFromPlan && !info.kept);
}

// ---------------------------------------------------------------------------
// PA-only helpers (encounter wrap-up). Not in pm-react; kept on the merge with
// pm-react's file (PROD-3312) because the wrap-up row and the line-remove hook
// import them.
// ---------------------------------------------------------------------------

/** PROD-2630: the order's own status, or null (the row renders "—"). Information only. */
export function orderStatusLabel(line: InvoiceOrderLine): string | null {
  return line.orderStatus?.trim() || null;
}

/** PROD-2630: who placed the order, or null (the row renders "—"), also when the name lookup failed. */
export function orderedByLabel(line: InvoiceOrderLine): string | null {
  return line.orderedBy?.name?.trim() || null;
}

export type InvoiceLineRemoval =
  | { kind: 'revert'; sourceOrderKey: string; name: string }
  | { kind: 'remove'; lineId: string };

/**
 * What the wrap-up row × does with this line: an order line goes back to Pending orders
 * (`Orders/revert`), any other line is removed (AddRemoveService `removeServices`).
 */
export function invoiceLineRemoval(services: readonly InvoiceService[] | undefined, lineId: string): InvoiceLineRemoval {
  const line = services?.find((s) => s.id === lineId);
  if (line && isOrderLine(line)) {
    const name = line.procedureCode?.displayName ?? line.shortDescription ?? 'The order';
    return { kind: 'revert', sourceOrderKey: line.sourceOrderKey, name };
  }
  return { kind: 'remove', lineId };
}
